import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";

/** Shared Play / Accept silhouette. */
export const LOBBY_MAIN_CTA_CLIP = `polygon(
    2.5% 0%, 97.5% 0%,
    99.4% 10.5%,
    94.7% 89.5%, 92.3% 100%,
    7.7% 100%, 5.3% 89.5%,
    0.6% 10.5%
)`;

/** Softer Accept silhouette — same family, less lateral tilt. */
const ACCEPT_CTA_CLIP = `polygon(
    1.35% 0%, 98.65% 0%,
    99.15% 9%,
    96.2% 91%, 95.1% 100%,
    4.9% 100%, 3.8% 91%,
    0.85% 9%
)`;

const PLAY_PUNCH_MS = 150;

const READY_FRAME_BG = `
    radial-gradient(
        ellipse 78% 85% at 50% -8%,
        rgba(255,247,215,0.55),
        transparent 58%
    ),
    linear-gradient(
        145deg,
        #F3D78A 0%,
        #D9A03A 22%,
        #945812 62%,
        #4A2506 100%
    )
`;

const READY_FACE_BG = `
    radial-gradient(
        ellipse 105% 92% at 50% -32%,
        rgba(255,246,213,0.42) 0%,
        rgba(255,211,116,0.14) 38%,
        transparent 66%
    ),
    linear-gradient(
        108deg,
        transparent 12%,
        rgba(255,244,211,0.03) 28%,
        rgba(255,230,178,0.1) 44%,
        rgba(255,204,101,0.03) 58%,
        transparent 76%
    ),
    linear-gradient(
        180deg,
        #D89A36 0%,
        #B87216 50%,
        #7A4008 100%
    )
`;

const READY_FACE_SHADOW = `
    inset 0 1px 0 rgba(255,250,230,0.42),
    inset 0 -3px 0 rgba(36,14,0,0.78),
    inset 0 0 0 1px rgba(255,208,112,0.16),
    inset 0 -20px 28px rgba(40,14,0,0.28)
`;

const READY_OUTER_SHADOW = `
    0 0 12px rgba(232,163,61,0.16),
    0 10px 22px rgba(120,70,12,0.22),
    0 6px 14px rgba(0,0,0,0.42),
    inset 0 1px 0 rgba(255,248,222,0.28)
`;

/** Matches lobby Play "missing"/disabled chrome. */
const DISABLED_FRAME_BG = `
    linear-gradient(
        145deg,
        rgba(255, 223, 160, 0.3) 0%,
        rgba(126, 82, 30, 0.38) 24%,
        rgba(48, 35, 20, 0.72) 66%,
        rgba(12, 13, 15, 0.96) 100%
    )
`;

const DISABLED_FACE_BG = `
    radial-gradient(
        ellipse 92% 76% at 50% -24%,
        rgba(232, 163, 61, 0.08),
        transparent 62%
    ),
    repeating-linear-gradient(
        90deg,
        rgba(255,255,255,0.018) 0px,
        rgba(255,255,255,0.018) 1px,
        transparent 1px,
        transparent 5px
    ),
    linear-gradient(
        180deg,
        #202124 0%,
        #17181B 46%,
        #0E0F11 100%
    )
`;

const DISABLED_FACE_SHADOW = `
    inset 0 1px 0 rgba(255, 231, 185, 0.1),
    inset 0 -2px 0 rgba(0, 0, 0, 0.62),
    inset 0 0 0 1px rgba(255, 255, 255, 0.025),
    inset 0 -14px 24px rgba(0, 0, 0, 0.18)
`;

const DISABLED_OUTER_SHADOW = `
    0 10px 24px rgba(0, 0, 0, 0.38),
    0 0 16px rgba(128, 82, 26, 0.1),
    inset 0 1px 0 rgba(255, 228, 176, 0.12)
`;

const ACCEPTED_FRAME_BG = "rgba(255,255,255,0.1)";

const ACCEPTED_FACE_BG = "rgba(12,13,14,0.92)";

const ACCEPTED_FACE_SHADOW = "none";

const ACCEPTED_OUTER_SHADOW = "none";

function triggerPlayHaptic() {
    try {
        if (
            typeof navigator !== "undefined" &&
            typeof navigator.vibrate === "function"
        ) {
            navigator.vibrate(14);
        }
    } catch (_) {
        // Non-fatal.
    }
}

/**
 * Gold main CTA in the Play family, tuned quieter for Accept Match.
 * Size / label typography are controlled by the caller via `sx` / `labelSx`.
 * Pass `disabled` for the Play-style muted chrome.
 * Pass `variant="accepted"` for the quiet CS2-style confirmed silhouette.
 */
export default function LobbyMainCta({
    label,
    onClick,
    disabled = false,
    variant = "default",
    sx = null,
    labelSx = null,
}) {
    const isAccepted = variant === "accepted";
    const isMuted = disabled && !isAccepted;
    const [isPunching, setIsPunching] = useState(false);
    const punchTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (punchTimerRef.current) {
                window.clearTimeout(punchTimerRef.current);
            }
        };
    }, []);

    const handleClick = (event) => {
        if (disabled) return;

        if (punchTimerRef.current) {
            window.clearTimeout(punchTimerRef.current);
        }
        setIsPunching(true);
        punchTimerRef.current = window.setTimeout(() => {
            setIsPunching(false);
            punchTimerRef.current = null;
        }, PLAY_PUNCH_MS);

        triggerPlayHaptic();
        onClick?.(event);
    };

    return (
        <Box
            className={`play-wrap${isPunching && !disabled ? " play-punch" : ""}`}
            sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                maxWidth: "100%",
                opacity: isMuted ? 0.92 : 1,
                "@keyframes playPunchBloom": {
                    "0%": { opacity: 0.22 },
                    "35%": { opacity: 0.55 },
                    "100%": { opacity: 0.28 },
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    width: "118%",
                    height: "140%",
                    borderRadius: "50%",
                    background: isAccepted || isMuted
                        ? "transparent"
                        : "radial-gradient(ellipse, rgba(232,163,61,0.09), transparent 70%)",
                    filter: "blur(14px)",
                    pointerEvents: "none",
                    transition: "opacity 0.15s ease-in-out",
                },
                "&.play-punch::before": {
                    background:
                        "radial-gradient(ellipse, rgba(255,214,120,0.22), transparent 72%)",
                    animation: "playPunchBloom 0.15s ease-out",
                },
            }}
        >
            <Box
                className={[
                    "btn-frame",
                    isMuted ? "playButtonDisabled" : "playButton",
                    isPunching && !disabled ? "is-punching" : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    p: isAccepted ? "1px" : "2px",
                    clipPath: ACCEPT_CTA_CLIP,
                    WebkitClipPath: ACCEPT_CTA_CLIP,
                    background: isAccepted
                        ? ACCEPTED_FRAME_BG
                        : isMuted
                          ? DISABLED_FRAME_BG
                          : READY_FRAME_BG,
                    boxShadow: isAccepted
                        ? ACCEPTED_OUTER_SHADOW
                        : isMuted
                          ? DISABLED_OUTER_SHADOW
                          : READY_OUTER_SHADOW,
                    cursor: disabled ? "default" : "pointer",
                    transition: `
                        box-shadow 0.15s ease-in-out,
                        filter 0.15s ease-in-out,
                        background 0.15s ease-in-out
                    `,
                    boxSizing: "border-box",
                    "&:hover": disabled || isAccepted
                        ? undefined
                        : {
                              filter: "brightness(1.06) saturate(1.04)",
                              boxShadow: `
                                  0 0 14px rgba(232,163,61,0.2),
                                  0 12px 26px rgba(120,70,12,0.26),
                                  0 8px 16px rgba(0,0,0,0.46),
                                  inset 0 1px 0 rgba(255,244,210,0.32)
                              `,
                              "& .btn-play": {
                                  boxShadow: `
                                      inset 0 1px 0 rgba(255,244,210,0.48),
                                      inset 0 -3px 0 rgba(36,14,0,0.82),
                                      inset 0 0 0 1px rgba(255,207,110,0.18),
                                      inset 0 -22px 30px rgba(36,14,0,0.3)
                                  `,
                              },
                              "& .play-shine": {
                                  opacity: 0.42,
                              },
                          },
                    "&:active, &.is-punching": disabled || isAccepted
                        ? undefined
                        : {
                              filter: "brightness(0.94) saturate(1.02)",
                              boxShadow: `
                                  0 0 10px rgba(232,163,61,0.18),
                                  0 4px 10px rgba(0,0,0,0.4),
                                  inset 0 1px 0 rgba(255,236,190,0.18)
                              `,
                              "& .btn-play": {
                                  filter: "brightness(0.96)",
                                  boxShadow: `
                                      inset 0 4px 12px rgba(32,12,0,0.62),
                                      inset 0 -1px 0 rgba(255,218,137,0.14),
                                      inset 0 0 0 1px rgba(255,196,90,0.12)
                                  `,
                              },
                              "& .play-shine": {
                                  opacity: 0.18,
                              },
                              "& .label": {
                                  textShadow: `
                                      0 1px 0 rgba(255, 231, 168, 0.22),
                                      0 -1px 1px rgba(20, 7, 0, 0.88)
                                  `,
                              },
                          },
                    ...sx,
                }}
            >
                <Box
                    component="button"
                    type="button"
                    disabled={disabled}
                    onClick={disabled ? undefined : handleClick}
                    className="btn-play"
                    aria-pressed={disabled || undefined}
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        clipPath: ACCEPT_CTA_CLIP,
                        WebkitClipPath: ACCEPT_CTA_CLIP,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        cursor: "inherit",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                        background: isAccepted
                            ? ACCEPTED_FACE_BG
                            : isMuted
                              ? DISABLED_FACE_BG
                              : READY_FACE_BG,
                        boxShadow: isAccepted
                            ? ACCEPTED_FACE_SHADOW
                            : isMuted
                              ? DISABLED_FACE_SHADOW
                              : READY_FACE_SHADOW,
                        transition:
                            "box-shadow 0.15s ease-in-out, filter 0.15s ease-in-out, background 0.15s ease-in-out",
                        "&:focus-visible": {
                            outline: disabled
                                ? "none"
                                : `2px solid rgba(255, 232, 174, 0.72)`,
                            outlineOffset: "-5px",
                        },
                    }}
                >
                    {!disabled && !isAccepted ? (
                        <>
                            <Box
                                aria-hidden
                                className="play-shine"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background: `
                                        linear-gradient(
                                            112deg,
                                            transparent 10%,
                                            rgba(255,250,232,0.04) 30%,
                                            rgba(255,242,207,0.22) 48%,
                                            rgba(255,218,143,0.05) 68%,
                                            transparent 88%
                                        )
                                    `,
                                    opacity: 0.32,
                                    transition: "opacity 180ms ease-in-out",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    left: "14%",
                                    right: "14%",
                                    top: 0,
                                    height: "1px",
                                    background: `
                                        linear-gradient(
                                            90deg,
                                            transparent,
                                            rgba(255,244,210,0.48),
                                            transparent
                                        )
                                    `,
                                    boxShadow: "0 1px 3px rgba(255,214,126,0.12)",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    left: "16%",
                                    right: "16%",
                                    bottom: 0,
                                    height: "1px",
                                    background: `
                                        linear-gradient(
                                            90deg,
                                            transparent,
                                            rgba(48,18,0,0.9),
                                            transparent
                                        )
                                    `,
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                        </>
                    ) : null}
                    <Box
                        component="span"
                        className="label"
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            fontFamily: "'Orbitron', sans-serif",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: isAccepted ? "0.35rem" : 0,
                            fontWeight: isAccepted ? 600 : disabled ? 700 : 900,
                            textTransform: "uppercase",
                            color: isAccepted
                                ? "rgba(198, 206, 200, 0.72)"
                                : disabled
                                  ? "rgba(221, 205, 178, 0.62)"
                                  : "#180D03",
                            lineHeight: 1,
                            WebkitTextStroke: isAccepted
                                ? "0"
                                : disabled
                                  ? "0.35px rgba(0, 0, 0, 0.48)"
                                  : "0.5px rgba(24, 8, 0, 0.58)",
                            textShadow: isAccepted
                                ? "none"
                                : disabled
                                  ? `
                                    0 -1px 1px rgba(0, 0, 0, 0.82),
                                    0 1px 0 rgba(255, 235, 203, 0.1)
                                `
                                  : `
                                    0 -1px 1px rgba(20, 7, 0, 0.82),
                                    0 1px 0 rgba(255, 231, 168, 0.36),
                                    0 2px 2px rgba(255, 196, 82, 0.08)
                                `,
                            ...labelSx,
                        }}
                    >
                        {isAccepted ? (
                            <CheckRounded
                                aria-hidden
                                sx={{
                                    fontSize: "1em",
                                    color: "rgba(160, 176, 164, 0.7)",
                                }}
                            />
                        ) : null}
                        {label}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

LobbyMainCta.propTypes = {
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(["default", "accepted"]),
    sx: PropTypes.object,
    labelSx: PropTypes.object,
};
